import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileUp, Info, Plus, X } from "lucide-react";
import { SectorSelectionList } from "@/components/risk/SectorSelectionList";
import { SectorCard } from "@/components/risk/SectorCard";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useGetSectors } from "@/hooks/api/query/useGetSectors";
import { SectorMismatchAlert } from "@/components/dashboard/SectorMismatchAlert";
import type { SectorAnalysis } from "@/types/risk-analysis";

type SectorSelectionStepProps = {
  selectedSectorIds: string[];
  sectorAnalysesMap: Record<string, SectorAnalysis>;
  companyName: string;
  onAddSectors: (sectorIds: string[]) => Promise<void>;
  onRemoveSector: (sectorId: string) => void;
  onNavigateToSector?: (sectorId: string) => void;
  navigateIsPending?: boolean;
  navigateIsLocked?: boolean;
  showFirstTimeBanner?: boolean;
  onDismissFirstTimeBanner?: () => void;
  notificareSectorNames?: string[];
  decizieSectorNames?: string[];
};

export function SectorSelectionStep({
  selectedSectorIds,
  sectorAnalysesMap,
  companyName,
  onAddSectors,
  onRemoveSector,
  onNavigateToSector,
  navigateIsPending,
  navigateIsLocked,
  showFirstTimeBanner,
  onDismissFirstTimeBanner,
  notificareSectorNames,
  decizieSectorNames,
}: SectorSelectionStepProps) {
  const navigate = useNavigate();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [stagedSectorIds, setStagedSectorIds] = useState<string[]>([]);
  const [deleteSectorId, setDeleteSectorId] = useState<string | null>(null);
  const { data: sectorsData } = useGetSectors({ context: "risk-analysis" });

  const handleToggleStagedSector = (sectorId: string) => {
    setStagedSectorIds((prev) =>
      prev.includes(sectorId)
        ? prev.filter((id) => id !== sectorId)
        : [...prev, sectorId]
    );
  };

  const handleConfirmAddSectors = async () => {
    await onAddSectors(stagedSectorIds);
    setStagedSectorIds([]);
    setShowAddDialog(false);
  };

  const handleOpenDialog = () => {
    setStagedSectorIds([]);
    setShowAddDialog(true);
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <h2 className="font-heading text-xl font-bold md:text-2xl">Sectoarele tale</h2>
          <div className="text-sm text-muted-foreground">
            <p>
              Companie: <span className="font-medium">{companyName}</span>
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate("/onboarding/analiza-risc")}
          className="shrink-0"
        >
          <FileUp className="w-4 h-4 mr-2" />
          Importă din XLSX
        </Button>
      </div>

      {showFirstTimeBanner && (
        <Alert className="border-blue-200 bg-blue-50 text-blue-800">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertDescription className="flex items-start justify-between gap-2 pl-1">
            <span>
              Sectoarele de mai jos au fost selectate automat pe baza
              răspunsurilor din formularul de identificare. Le poți modifica
              oricând.
            </span>
            <button
              onClick={onDismissFirstTimeBanner}
              className="shrink-0 text-blue-600 hover:text-blue-800"
              aria-label="Închide"
            >
              <X className="h-4 w-4" />
            </button>
          </AlertDescription>
        </Alert>
      )}

      {notificareSectorNames && decizieSectorNames && (
        <SectorMismatchAlert
          notificareSectors={notificareSectorNames}
          decizieSectors={decizieSectorNames}
        />
      )}

      <div className="grid gap-3 md:gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {selectedSectorIds.map((sectorId) => {
          const analysis = sectorAnalysesMap[sectorId];
          const sector = sectorsData?.find((s) => s.id === sectorId);
          if (!sector) return null;

          return (
            <SectorCard
              key={sectorId}
              sector={sector}
              analysis={analysis}
              onNavigate={() => onNavigateToSector?.(sectorId)}
              onDelete={() => setDeleteSectorId(sectorId)}
              isNavigatePending={navigateIsPending}
              isNavigateLocked={navigateIsLocked}
              className="h-full"
            />
          );
        })}

        <Card
          className="flex justify-center border-dashed cursor-pointer hover:border-primary hover:bg-muted/50 transition-colors"
          onClick={handleOpenDialog}
        >
          <CardContent className="flex flex-col items-center justify-center min-h-[150px] md:min-h-[200px] gap-2 text-muted-foreground">
            <Plus className="h-6 w-6 md:h-8 md:w-8" />
            <span className="text-sm font-medium">Adaugă sector</span>
          </CardContent>
        </Card>
      </div>

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg max-h-full overflow-auto">
          <DialogHeader>
            <DialogTitle className="text-sm md:text-base">
              Adaugă sectoare noi
            </DialogTitle>
            <DialogDescription className="text-xs md:text-sm">
              Selectează sectoarele pe care dorești să le adaugi la analiza de
              risc.
            </DialogDescription>
          </DialogHeader>
          <SectorSelectionList
            selectedSectors={stagedSectorIds}
            onToggle={handleToggleStagedSector}
            showSearch={true}
            inModal={true}
            disabledSectors={selectedSectorIds}
          />
          <DialogFooter>
            <Button
              onClick={handleConfirmAddSectors}
              disabled={stagedSectorIds.length === 0}
            >
              Adaugă
              {stagedSectorIds.length > 0 ? ` (${stagedSectorIds.length})` : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={deleteSectorId !== null}
        onOpenChange={(open) => !open && setDeleteSectorId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ștergere sector</AlertDialogTitle>
            <AlertDialogDescription>
              Sunteți sigur că doriți să ștergeți acest sector? Această acțiune
              nu poate fi anulată.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anulează</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteSectorId) onRemoveSector(deleteSectorId);
                setDeleteSectorId(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Șterge
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
